import {useContext, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import Icon from 'patient-ping-remedy/packages/icon';
import ToolTip from 'patient-ping-remedy/packages/tool-tip';
import InfoIconTooltip from '@common/InfoIconTooltip';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {FilterResponse, FilterView} from '@/api/dto/filters';
import Helpers from '@/helpers/helpers';
import {formatTaskFiltersForApi} from '@/api/dto/care-journey';
import {
  SummaryWrapper,
  SummaryHeader,
  SummaryTitle,
  CollapsedSummaryText,
  TilesContainer,
  Tile,
  TileCount,
  TileLabel,
  ArrowWrapper,
  TilesDivider, PinnedTileContainer, StaticTileContainer,
} from '@/components/pages/tasks/WorkSummary.styles';

type Props = {
  onAssignedClick: (currentUserUuid: string) => void;
  onOverdueClick: (currentUserUuid: string) => void;
  onPinnedFilterClick: (filter: FilterResponse) => void;
};

export default function WorkSummary({ onAssignedClick, onOverdueClick, onPinnedFilterClick }: Props) {
  const {carecoApi} = useContext(CarecoApiContext);
  const [isExpanded, setIsExpanded] = useState(true);

  const {data: filters} = useQuery({
    queryKey: ['filters', FilterView.TASKS_TABLE],
    queryFn: () => carecoApi?.getFilters(FilterView.TASKS_TABLE),
    enabled: !!carecoApi,
  });

  const pinnedFilters = (filters ?? []).filter((f) => f.isPinned);

  const pinnedSummaryFilters = pinnedFilters.map((f) => {
    const data: Record<string, any> = Helpers.safeJsonParse(f.data, {});
    const extracted = Helpers.extractFilterValues(data);
    return { id: f.id, filters: formatTaskFiltersForApi(extracted) };
  });

  const {data: summary} = useQuery({
    queryKey: ['taskSummary', pinnedSummaryFilters],
    queryFn: () => carecoApi?.getTaskSummary({
      pinnedFilters: pinnedSummaryFilters,
    }),
    enabled: !!carecoApi,
  });

  const assignedCount = summary?.assignedTaskCount ?? 0;
  const overdueCount = summary?.overdueTaskCount ?? 0;

  return (
    <SummaryWrapper>
      <SummaryHeader onClick={() => setIsExpanded(!isExpanded)} data-testid="work-summary-header">
        <SummaryTitle>Your Work Summary</SummaryTitle>
        <InfoIconTooltip
          text={'This is a summary of your to-do list. You can pin up to 6 of your most used'
            + ' saved views here. To pin or unpin a view, use the "Edit Saved View" panel.'}
          position="right" 
        />
        {!isExpanded && (
          <CollapsedSummaryText data-testid="work-summary-collapsed-text">
            {assignedCount} Total assigned tasks | {overdueCount} Overdue
          </CollapsedSummaryText>
        )}
        <ArrowWrapper>
          <Icon iconClass={isExpanded ? 'angle-up' : 'angle-down'} weight="far" iconSize="lg" />
        </ArrowWrapper>
      </SummaryHeader>
      {isExpanded && (
        <TilesContainer data-testid="work-summary-tiles">
          <StaticTileContainer>
            <Tile onClick={
              () => summary?.currentUserUuid && onAssignedClick(summary.currentUserUuid)} data-testid="tile-assigned">
              <TileCount>{assignedCount}</TileCount>
              <ToolTip text="Total assigned tasks" position="bottom" type="dark">
                <TileLabel>Total assigned tasks</TileLabel>
              </ToolTip>
            </Tile>
            <Tile onClick={() => summary?.currentUserUuid && onOverdueClick(summary.currentUserUuid)} data-testid="tile-overdue">
              <TileCount>{overdueCount}</TileCount>
              <ToolTip text="Overdue" position="bottom" type="dark">
                <TileLabel>Overdue</TileLabel>
              </ToolTip>
            </Tile>
          </StaticTileContainer>
          {pinnedFilters.length > 0 && (
            <PinnedTileContainer>
              <TilesDivider />
              {pinnedFilters.map((filter) => (
                <Tile
                  small
                  key={filter.id}
                  onClick={() => onPinnedFilterClick(filter)}
                  data-testid={`tile-pinned-${filter.id}`}
                >
                  <TileCount>{summary?.pinnedFilterCounts?.find(p => p.id === filter.id)?.count ?? 0}</TileCount>
                  <ToolTip text={filter.name} position="bottom" type="dark">
                    <TileLabel>{filter.name}</TileLabel>
                  </ToolTip>
                </Tile>
              ))}
            </PinnedTileContainer>)}

        </TilesContainer>
      )}
    </SummaryWrapper>
  );
}
